import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { useLocalization } from '@fluent/react';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PeopleIcon from '@mui/icons-material/People';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ImportantDevicesIcon from '@mui/icons-material/ImportantDevices';
import { Divider, List, ListItemIcon, ListItemText, ListItemButton as MuiListItemButton } from '@mui/material';
import { forwardRef, useCallback, useMemo } from 'react';
import GavelIcon from '@mui/icons-material/Gavel';
import IsoIcon from '@mui/icons-material/Iso';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import React from 'react';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import { GridView } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from './hooks';
import { selectModule } from '../features/modules/moduleSlice';
import Diversity2Icon from '@mui/icons-material/Diversity2';
import { Tooltip } from "@mui/material";
import EmailIcon from '@mui/icons-material/Email';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GradingIcon from '@mui/icons-material/Grading';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import FileOpenOutlinedIcon from '@mui/icons-material/FileOpenOutlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import { logout } from '../features/user/userSlice';
import Inventory2Icon from "@mui/icons-material/Inventory2";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DnsIcon from '@mui/icons-material/Dns';
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import StorefrontIcon from '@mui/icons-material/Storefront';
import DomainIcon from '@mui/icons-material/Domain';
import ArchiveIcon from '@mui/icons-material/Archive';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SensorsIcon from '@mui/icons-material/Sensors';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import { CompanyFeatureFlags, FeatureFlagGuard } from '../features/settings/featureFlags';
//import { Can } from "../features/permissions/useCan";
import { Package } from "../features/permissions/usePackage";

interface ListItemLinkProps {
    icon?: React.ReactElement
    primary: string
    to: string
    disabled?: boolean
}

function ListItemLink(props: ListItemLinkProps) {
    const { icon, primary, to, disabled } = props;

    const renderLink = useMemo(
        () => forwardRef<any, Omit<RouterLinkProps, 'to'>>(
            (itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
        [to],
    );

    return <li>
        <MuiListItemButton disabled={disabled ?? false} component={renderLink}>
            <Tooltip title={primary}><ListItemIcon>{icon}</ListItemIcon></Tooltip>
            <ListItemText primary={primary} />
        </MuiListItemButton>
    </li>
}


interface ListItemButtonProps {
    icon?: React.ReactElement
    primary: string
    onClick: () => void
    disabled?: boolean
}
function ListItemButton(props: ListItemButtonProps) {
    const { onClick, primary, icon, disabled } = props;

    return <li>
        <MuiListItemButton disabled={disabled ?? false} onClick={onClick}>
            <Tooltip title={primary}><ListItemIcon>{icon}</ListItemIcon></Tooltip>
            <ListItemText primary={primary} />
        </MuiListItemButton>
    </li>
}

export const Nav = () => {
    const module = useAppSelector(state => selectModule(state).currentModule?.code)

    switch (module) {
        case 'super':
            return <SuperNav />
        case 'admin':
            return <AdminNav />
        case 'validation':
            return <ValidationNav />
        case 'urs':
            return <URSNav />
        case 'offering':
            return <OfferingNav />
        case 'project':
            return <ProjectManagementNav />
        case 'measure':
            return <MeasureNav />
        case 'demo':
            return <DemoNav />
        case 'marketplace':
            return <MarketplaceNav />
        case 'qms':
            return <QmsNav />
        default:
            return <MeasureNav />
    }
}

const SuperNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/"
            primary={l10n.getString("menu-homepage", null, "Strona główna")}
            icon={<HomeIcon />}
        />
        <ListItemLink
            to="/structure"
            primary={l10n.getString("menu-structure", null, "Struktura")}
            icon={<GridView />}
        />
        <ListItemLink
            to="/companies"
            primary={l10n.getString("menu-companies", null, "Firmy")}
            icon={<BusinessIcon />}
        />
        <ListItemLink
            to="/companies-to-register"
            primary={l10n.getString("menu-companies-to-register", null, "Firmy do zarejestrowania")}
            icon={<DomainAddIcon />}
        />
        <ListItemLink
            to="/users"
            primary={l10n.getString("menu-users", null, "Użytkownicy")}
            icon={<PeopleIcon />}
        />
        <ListItemLink
            to="/archived-users"
            primary={l10n.getString("menu-archived-users", null, "Zarchiwizowani użytkownicy")}
            icon={<PeopleOutlineIcon />}
        />
        <ListItemLink
            to="/documents-overview"
            primary={l10n.getString("menu-documents-overview", null, "Documenty")}
            icon={<GridView />}
        />
        <ListItemLink
            to="/modules/access"
            primary={l10n.getString("menu-modules-access", null, "Dostęp do modułów")}
            icon={<InventoryIcon />}
        />
        <ListItemLink
            to="/sys/validations"
            primary={l10n.getString("menu-validations", null, "Walidacje")}
            icon={<PlaylistAddCheckIcon />}
        />
        <ListItemLink
            to="/sys/requirements"
            primary={l10n.getString("menu-requirements-dictionary", null, "Urs requirements")}
            icon={<IsoIcon />}
        />
        <ListItemLink
            to="/sys/requirements-in-structure"
            primary={l10n.getString("menu-requirements-in-structure", null, "_Requirements by structure")}
            icon={<AccountTreeIcon />}
        />
        <ListItemLink
            to="/requirementsTypes"
            primary={l10n.getString("menu-requirements-types", null, "Typy wymagań")}
            icon={<IsoIcon />}
        />
        <ListItemLink
            to="/sys/urstemplates"
            primary={l10n.getString("menu-urs-templates", null, "Szablony URS")}
            icon={<HowToVoteIcon />}
        />
        <ListItemLink
            to="/sys/document-templates"
            primary={l10n.getString("menu-document-templates", null, "Szablony documentow")}
            icon={<HowToVoteIcon />}
        />
        <ListItemLink
            to="/sys/document-dictionaries"
            primary={l10n.getString("menu-document-dictionaries", null, "Słowniki dokumentów")}
            icon={<LibraryBooksIcon />}
        />

        <ListItemLink
            to="/sys/aisettings"
            primary={l10n.getString("menu-ai-settings", null, "Ustawienia AI")}
            icon={<Diversity2Icon />}
        />
        <ListItemLink
            to="/sys/mailingsettings"
            primary={l10n.getString("menu-mailing-settings", null, "Ustawienia Mailingu")}
            icon={<EmailIcon />}
        />
        <ListItemLink
            to="/sys/releases"
            primary={l10n.getString("menu-releases", null, "_Releases")}
            icon={<Inventory2Icon />}
        />
        <ListItemLink
            to="/system-settings"
            primary={l10n.getString("menu-system-settings", null, "Ustawienia systemowe")}
            icon={<SettingsSuggestIcon />}
        />
        <ListItemLink
            to="/sys/workflow-settings"
            primary={l10n.getString("menu-workflow-settings", null, "Ustawienia workflow")}
            icon={<SettingsSuggestIcon />}
        />
        <Divider />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />
    </List>
}

const ValidationNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/"
            primary={l10n.getString("menu-homepage", null, "Strona główna")}
            icon={<HomeIcon />}
        />
        <Package name="Projects">
            <ListItemLink
                to="/validation-project"
                primary={l10n.getString("menu-projects-list", null, "Projekty")}
                icon={<DashboardIcon />}
            />
        </Package>
        <ListItemLink
            to="/validation-processes"
            primary={l10n.getString("menu-processes", null, "_Lista procesów")}
            icon={<AccountTreeIcon />}
        />
        <ListItemLink
            to="/devices"
            primary={l10n.getString("menu-devices", null, "Lista urzadzeń")}
            icon={<ImportantDevicesIcon />}
        />
        <ListItemLink
            to="/computerized-systems"
            primary={l10n.getString("menu-computerized-systems", null, "Systemy skomputeryzowane")}
            icon={<DnsIcon />}
        />
        {/* <ListItemLink
            to="/room-groups"
            primary={l10n.getString("menu-room-groups", null, "Grupy pomieszczeń")}
            icon={<MeetingRoomIcon />}
        /> */}
        {/* <ListItemLink
            to="/validations"
            primary={l10n.getString("menu-validations", null, "Walidacje")}
            icon={<PlaylistAddCheckIcon />}
        /> */}

        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />

    </List>
}

const URSNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/"
            primary={l10n.getString("menu-homepage", null, "Strona główna")}
            icon={<HomeIcon />}
        />
        <Package name="URS">
            <ListItemLink
                to="/urs"
                primary={l10n.getString("menu-urs", null, "URS")}
                icon={<GavelIcon />}
            />
        </Package>
        <Package name="RiskAnalysis">
            <ListItemLink
                to="/new-risk-analysis"
                primary={l10n.getString("risk-analysis", null, "risk-analysis")}
                icon={<TroubleshootOutlinedIcon />}
            />
        </Package>
        <Package name="UrsTemplates">
            <ListItemLink
                to="/urstemplates"
                primary={l10n.getString("menu-urs-templates", null, "Szablony URS")}
                icon={<HowToVoteIcon />}
            />
        </Package>
        <Package name="CompanySupplierDictionary">
            <ListItemLink
                to="/supplier"
                primary={l10n.getString("menu-supplier", null, "Producenci")}
                icon={<BusinessIcon />}
            />
        </Package>
        <FeatureFlagGuard flag={CompanyFeatureFlags.CatalogRequierment}>
            <ListItemLink
                to="/requirements"
                primary={l10n.getString("menu-requirements-dictionary", null, "_Katalog wymagań")}
                icon={<IsoIcon />}
            />
            <ListItemLink
                to="/requirements-in-structure"
                primary={l10n.getString("menu-requirements-in-structure", null, "_Requirements by structure")}
                icon={<AccountTreeIcon />}
            />
        </FeatureFlagGuard>
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />

    </List>
}

const QmsNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/"
            primary={l10n.getString("menu-homepage", null, "Strona główna")}
            icon={<HomeIcon />}
        />
        <ListItemLink
            to="/qms-documents"
            primary={l10n.getString("menu-qms-documents", null, "QMS Documents")}
            icon={<GavelIcon />}
        />
        <ListItemLink
            to="/qms-documents-templates"
            primary={l10n.getString("menu-qms-documents-templates", null, "QMS Documents Templates")}
            icon={<GavelIcon />}
        />
        <ListItemLink
            to="/qms-reports"
            primary={l10n.getString("menu-qms-reports", null, "QMS Reports")}
            icon={<GavelIcon />}
        />
        <ListItemLink
            to="/company-structures"
            primary={l10n.getString("menu-company-structures", null, "Struktury")}
            icon={<BusinessIcon />}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />

    </List>
}


const MarketplaceNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/"
            primary={l10n.getString("menu-homepage", null, "Strona główna")}
            icon={<HomeIcon />}
        />
        <ListItemLink
            to="/marketplace"
            primary={l10n.getString("menu-marketplace", null, "Marketplace")}
            icon={<StorefrontIcon />}
        />
        <ListItemLink
            to="/company-profile"
            primary={l10n.getString("menu-company-profile", null, "Profil firmy")}
            icon={<DomainIcon />}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />

    </List>
}

const AdminNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const isDemoLicense = useAppSelector(state => selectModule(state).license === 'DEMO')
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/"
            primary={l10n.getString("menu-homepage", null, "Strona główna")}
            icon={<HomeIcon />}
        />
        <ListItemLink
            to="/company/users"
            primary={l10n.getString("menu-users", null, "Użytkownicy")}
            icon={<PeopleIcon />}
        />
        <ListItemLink
            to="/company/roles"
            primary={l10n.getString("menu-company-roles", null, "Role w firmie")}
            icon={<AdminPanelSettingsIcon />}
        />
        <ListItemLink
            to="/company/archived-users"
            primary={l10n.getString("menu-archived-users", null, "Zarchiwizowani użytkownicy")}
            icon={<PeopleOutlineIcon />}
        />
        <ListItemLink
            to="/company/user-requests"
            primary={l10n.getString("menu-user-requests", null, "_Prośby użytkownika")}
            icon={<OutlinedFlagIcon />}
        />
        <ListItemLink
            to="/company/settings"
            primary={l10n.getString("menu-company-settings", null, "Ustawienia firmowe")}
            icon={<SettingsIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/company/system-information"
            primary={l10n.getString("menu-company-system-information", null, "Status systemu")}
            icon={<SensorsIcon />}
        />
        <ListItemLink
            to="/client-document-templates"
            primary={l10n.getString("menu-document-templates", null, "_templates")}
            icon={<LibraryBooksIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/company-dictionaries"
            primary={l10n.getString("menu-company-dictionaries", null, "Słowniki firmowe")}
            icon={<MenuBookIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/company/form-definitions"
            primary={l10n.getString("menu-form-definitions", null, "Formularze")}
            icon={<DynamicFormIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/company/documents-overview"
            primary={l10n.getString("menu-company-documents-overview", null, "_Documents")}
            icon={<GridView />}
        />
        <ListItemLink
            to="/mailingsettings"
            primary={l10n.getString("menu-mailing-settings", null, "Ustawienia Mailingu")}
            icon={<EmailIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/company/system-events"
            primary={l10n.getString("menu-system-events", null, "Zdarzenia systemowe")}
            icon={<EventRepeatIcon />}
        />
        <ListItemLink
            to="/company-structure-definitions"
            primary={l10n.getString("menu-company-structure-definitions", null, "Definicje struktury firmowej")}
            icon={<AccountTreeIcon />}
        />
        <ListItemLink
            to="/aisettings"
            primary={l10n.getString("menu-ai-settings", null, "Ustawienia AI")}
            icon={<Diversity2Icon />}
            disabled={isDemoLicense}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />
    </List>
}

const MeasureNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/measurements"
            primary={l10n.getString("menu-measurements", null, "Pomiary")}
            icon={<HomeIcon />}
        />
        <ListItemLink
            to="/measurements/sensors"
            primary={l10n.getString("menu-sensors", null, "Czujniki")}
            icon={<SensorsIcon />}
        />
        <ListItemLink
            to="/measurements/cleaning-rooms"
            primary={l10n.getString("menu-cleaning-rooms", null, "Pomieszczenia czyste")}
            icon={<MeetingRoomIcon />}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />
    </List>
}

const OfferingNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const isDemoLicense = useAppSelector(state => selectModule(state).license === 'DEMO')
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <ListItemLink
            to="/imported-documents"
            primary={l10n.getString("menu-imported-documents", null, "_Zaimportowane dokumenty")}
            icon={<FileOpenOutlinedIcon />}
        />
        <ListItemLink
            to="/ursOffers"
            primary={l10n.getString("menu-urs-offers", null, "Zapytania URS")}
            icon={<HelpCenterIcon />}
        />
        <ListItemLink
            to="/supplier-awarded-urs"
            primary={l10n.getString("menu-awarded-urs", null, "_Awarded URS")}
            icon={<HistoryEduIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/supplier-doc-process"
            primary={l10n.getString("menu-documentation-process", null, "Doc processes")}
            icon={<DashboardIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/supplier-devices"
            primary={l10n.getString("menu-supplier-sold-devices", null, "Lista urzadzeń")}
            icon={<ImportantDevicesIcon />}
            disabled={isDemoLicense}
        />
        {/* <ListItemLink
            to="/supplier-imported-urs"
            primary={l10n.getString("menu-supplier-imported-urs-list", null, "_Zaimportowane URS")}
            icon={<FileOpenOutlinedIcon />}
        /> */}


        <ListItemLink
            to="/specs"
            primary={l10n.getString("menu-specs", null, "Specyfikacje")}
            icon={<GradingIcon />}
            disabled={isDemoLicense}
        />
        {/* <ListItemLink
            to="/supplier-risk-analysis"

            primary={l10n.getString("risk-analysis", null, "risk-analysis")}
            icon={<TroubleshootOutlinedIcon />}
            disabled={isDemoLicense}
        /> */}

        <ListItemLink
            to="/supplierdevices"
            primary={l10n.getString("menu-supplier-offer-template", null, "_Szablony ofert")}
            icon={<ReceiptLongIcon />}
            disabled={isDemoLicense}
        />
        <ListItemLink
            to="/suppliercounterparts"
            primary={l10n.getString("menu-supplier-contacts", null, "_Kontrahenci")}
            icon={<PeopleIcon />}
            disabled={isDemoLicense}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />
    </List>
}


const ProjectManagementNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <Package name="Projects">
            <ListItemLink
                to="/projects"
                primary={l10n.getString("menu-projects-list", null, "Projekty")}
                icon={<DashboardIcon />}
            />
        </Package>
        <Divider />
        {/* <ListItemLink
            to="/projectprocess"
            primary={l10n.getString("menu-project-process-list", null, "Procesy")}
            icon={<AssignmentIcon />}
        /> */}
        <Package name="BusinessCase">
            <ListItemLink
                to="/businessCases"
                primary={l10n.getString("menu-business-cases", null, "Business cases")}
                icon={<AssignmentIcon />}
            />
        </Package>
        <ListItemLink
            to="/changeControlDocuments"
            primary={l10n.getString("menu-chc-list", null, "Change control")}
            icon={<AssignmentIcon />}
        />
        {/* <ListItemLink
            to="/materialcards"
            primary={l10n.getString("menu-material-cards", null, "Karty materiałowe")}
            icon={<ListAltOutlinedIcon />}
        /> */}
        <Divider />
        <Package name="ProjectArchive">
            <ListItemLink
                to="/archived-projects"
                primary={l10n.getString("menu-archived-projects-list", null, "Archiwalne projekty")}
                icon={<ArchiveIcon />}
            />
        </Package>
        <ListItemLink
            to="/operational-dashboards"
            primary={l10n.getString("menu-operational-dashboards", null, "_Dashboardy operacyjne")}
            icon={<DashboardIcon />}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />
    </List>
}

const DemoNav = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const handleLogout = useCallback(() => {
        dispatch(logout())
    }, [])
    return <List>
        <Package name="URS">
            <ListItemLink
                to="/dynamic-urs"
                primary={l10n.getString("menu-urs", null, "URS")}
                icon={<GavelIcon />}
            />
        </Package>
        <ListItemLink
            to="/custom-processes"
            primary={l10n.getString("menu-custom-processes", null, "Procesy dokuementacyjne")}
            icon={<GridView />}
        />
        <ListItemLink
            to="/workflow-definitions"
            primary={l10n.getString("menu-workflow-definitions", null, "Definicje workflow")}
            icon={<GridView />}
        />
        <ListItemLink
            to="/workflow-roles"
            primary={l10n.getString("menu-workflow-roles", null, "Role workflow")}
            icon={<GridView />}
        />
        <Divider />
        <ListItemLink
            to="/modules"
            primary={l10n.getString("menu-modules", null, "Zmień moduł")}
            icon={<ChangeCircleIcon />}
        />
        <ListItemLink
            to="/settings"
            primary={l10n.getString("menu-settings", null, "Ustawienia")}
            icon={<SettingsIcon />}
        />
        <ListItemButton
            onClick={handleLogout}
            primary={l10n.getString("menu-logout", null, "Wyloguj")}
            icon={<PowerSettingsNewIcon />}
        />
    </List>
}
